import Header from '@/components/Header'
import useDocumentTitle from '@/components/Helmet'
import { useCustomLoader } from '@/components/Loader'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import axiosInstance from '@/lib/axiosInstance'
import { formatTick } from '@/lib/utils'
import { Chip, Grid, Paper, Typography } from '@mui/material'
import { Building, User, Users } from 'lucide-react'
import React, { useEffect, useMemo, useState } from 'react'
import ReactApexChart from 'react-apexcharts';

const UNIT_LABELS = [
    { key: 'day', label: 'Day' },
    { key: 'week', label: 'Week' },
    { key: 'month', label: 'Month' },
    { key: 'year', label: 'Year' },
];

const AdminDashboard = () => {
    useDocumentTitle("Admin Dashboard");
    const loader = useCustomLoader();
    const { toast } = useToast();
    const [counts, setCounts] = useState({
        users: {
            total: 0,
            buyers: 0,
            sellers: 0,
            agents: 0
        },
        properties: {},
        inquires: {}
    });
    const [series, setSeries] = useState([]);
    const [range, setRange] = useState({ start: new Date(), end: new Date() });
    const [loading, setLoading] = useState(false);
    const [errorTS, setErrorTS] = useState('');
    const [unit, setUnit] = useState('day');
    const [mounted, setMounted] = useState(true);
    const fetchLineChartData = async () => {
        await loadTS();
    }
    const fetchCounts = async () => {
        loader(true);
        try {
            await axiosInstance.get('/api/admin/counts')
                .then(res => {
                    setCounts(res.data)
                })
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        }
        finally {
            loader(false);
        }
    }
    async function loadTS() {
        setLoading(true);
        setErrorTS('');
        try {
            const { data } = await axiosInstance.get(`/api/admin/timeseries`, { params: { unit } });
            const dataPoints = data.buckets.map((b: any) => ({
                time: new Date(b.ts).getTime(),
                Users: b.users,
                Properties: b.properties,
            }));
            setSeries(dataPoints);
            setRange({ start: data.start, end: data.end });
        } catch (e) {
            toast({
                variant: "destructive",
                title: "Error!",
                description: "Something went wrong",
            })
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => {
        fetchCounts();
    }, [])
    useEffect(() => {
        fetchLineChartData()
    }, [unit])

    const options: any = useMemo(() => ({
        chart: { type: 'line', height: 320, animations: { enabled: false } },
        stroke: { curve: 'smooth' },
        xaxis: {
            type: 'datetime',
            tickAmount: unit === 'day' ? 6 : unit === 'year' ? 12 : 7,
            labels: {
                datetimeUTC: false, // let it display in local tz
            },
        },

        yaxis: { decimalsInFloat: 0 },
        tooltip: { x: { format: unit === 'day' ? 'HH:mm' : unit === 'year' ? 'MMM yyyy' : 'dd MMM' } },
        legend: { show: true },
    }), [unit, range]);

    const apexSeries = [
        { name: 'Users', data: series.map((p: any) => [p.time, p.Users]) },
        { name: 'Properties', data: series.map((p: any) => [p.time, p.Properties]) },
    ];

    function FilterTabs({ unit, onChange }: any) {
        return (
            <div className='p-2'>
                <Select value={unit} onValueChange={(e) => onChange(e)}>
                    <SelectTrigger className="text-gray-900 text-sm w-28" data-testid="select-user-type" >
                        <SelectValue placeholder="Select Filter" />
                    </SelectTrigger>
                    <SelectContent>
                        {UNIT_LABELS.map((u) => (
                            <SelectItem
                                key={u.key}
                                value={u.key}
                            >
                                {u.label}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }
    return (
        <div className='min-h-screen bg-gray-50'>
            <Header />
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ borderRadius: 5, background: 'linear-gradient(to right, #00db70, #0075b0)' }}>
                            <div className="p-4">
                                <div className='w-full flex justify-center'>
                                    <User className="w-12 h-12 text-gray-700" />
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <h3 className="text-2xl font-semibold mb-2 text-white">Total Buyer</h3>
                                    <Chip label={counts.users?.buyers} sx={{ color: "white", fontSize: 20 }} />
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ borderRadius: 5, background: 'linear-gradient(90deg, hsla(192, 80%, 51%, 1) 0%, hsla(355, 85%, 63%, 1) 100%)' }}>
                            <div className="p-4">
                                <div className='w-full flex justify-center'>
                                    <User className="w-12 h-12 text-gray-700" />
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <h3 className="text-2xl font-semibold mb-2 text-white">Total Seller</h3>
                                    <Chip label={counts.users?.sellers} sx={{ color: "white", fontSize: 20 }} />
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={{ borderRadius: 5, background: 'linear-gradient(to right, #00dbab, #00b01f)' }}>
                            <div className="p-4">
                                <div className='w-full flex justify-center'>
                                    <User className="w-12 h-12 text-gray-700" />
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <h3 className="text-2xl font-semibold mb-2 text-white">Total Agent</h3>
                                    <Chip label={counts.users?.agents} sx={{ color: "white", fontSize: 20 }} />
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ borderRadius: 5, background: 'linear-gradient(to right, #00b4db, #0083b0)' }}>
                            <div className="p-4">
                                <div className='w-full flex justify-center'>
                                    <Users className="w-12 h-12 text-gray-700" />
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <h3 className="text-2xl font-semibold mb-2 text-white">Total User</h3>
                                    <Chip label={counts.users?.total} sx={{ color: "white", fontSize: 20 }} />
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ borderRadius: 5, background: 'linear-gradient(90deg, hsla(259, 84%, 78%, 1) 0%, hsla(206, 67%, 75%, 1) 100%)' }}>
                            <div className="p-4">
                                <div className='w-full flex justify-center'>
                                    <Building className="w-12 h-12 text-gray-700" />
                                </div>
                                <div className='flex justify-between mt-3'>
                                    <h3 className="text-2xl font-semibold mb-2 text-white">Total Properties</h3>
                                    <Chip label={counts.users?.agents} sx={{ color: "white", fontSize: 20 }} />
                                </div>
                            </div>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 12 }}>
                        <Paper style={{ minHeight: 320 }}>
                            <div className='flex justify-between'>
                                <Typography variant="h6" className="p-4 font-semibold">Activity</Typography>
                                <FilterTabs unit={unit} onChange={setUnit} />
                            </div>
                            <ReactApexChart options={options} series={apexSeries} type="line" height={320} />
                        </Paper>
                    </Grid>
                </Grid>
            </section>
        </div>
    )
}

export default AdminDashboard